import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { clearMessage } from "../../redux/Slice/message";
import { setLoading } from "../../redux/Slice/loaderSlice";
import { getB2bLead, updateB2bLead } from "../../redux/Slice/b2bLeadSlice";
import CustomTable from "../../components/table/Table";

const columns = [
  { id: "temp_id", label: "Template No", minWidth: 150, align: "start" },
  { id: "name", label: "Template Name", minWidth: 150, align: "start" },
  { id: "status", label: "Status", minWidth: 150, align: "center" },
  {
    id: "action",
    label: "Action",
    minWidth: 150,
    align: "center",
    actionType: [{ edit: true, view: true }],
  },
];

const LeadTemplates = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modifiedRows, setModifiedRows] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const message = useSelector((state) => state.message);
  const { b2bLeads, totalCount } = useSelector((state) => state.b2bLead);

  // Transform the fetched b2b leads to table rows
  const transformB2bleads = (b2bLeads) => b2bLeads
      .filter((lead) => lead && lead._id)
      .map((lead) => ({
        _id: lead._id,
        temp_id: lead.temp_id,
        name: lead.templateName,
        status: lead.status ? true : false,
        action: "",
      }));

  useEffect(() => {
    if (Array.isArray(b2bLeads)) {
      const transformedLeads = transformB2bleads(b2bLeads);
      setModifiedRows(transformedLeads);
    }
  }, [b2bLeads]);

  // Display messages using toast
  useEffect(() => {
    if (message?.message) {
      toast(message.message);
      dispatch(clearMessage());
    }
  }, [message, dispatch]);

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(getB2bLead({ page, limit: rowsPerPage, searchTerm }))
      .then(() => {
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  }, [dispatch, page, rowsPerPage, searchTerm]);

  const handleStatusChange = (row) => {
    const updatedStatus = !row.status;
    const b2bLeadId = row._id;

    dispatch(setLoading(true));
    dispatch(updateB2bLead({ l_id: b2bLeadId, formData: { status: updatedStatus } }))
      .unwrap()
      .then(() => {

        // Update the modifiedRows locally
        const updatedRows = modifiedRows.map((item) =>
          item._id === b2bLeadId ? { ...item, status: updatedStatus } : item
        );
        setModifiedRows(updatedRows);
      })
      .catch((error) => {
        console.error("Failed to update status:", error);
        toast.error("Failed to update status. Please try again.");
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const handleEdit = (row) => {
    if (row._id) {
      navigate(`/leadtemplate/edit/${row._id}`);
    } else {
      console.log("No lead ID found for this row", row);
    }
  };

  const handleView = (row) => {
    if (row._id) {
      navigate(`/leadtemplate/${row._id}`);
    } else {
      console.log("No lead ID found for this row", row);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div className="content">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ mb: 3, mt: 3 }}>
          Lead Templates{" "}
          <Typography component="span">({totalCount})</Typography>
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            placeholder="Search by Name"
            value={searchTerm}
            onChange={handleSearch}
            fullWidth
          />
          <Link to="/leadtemplate/create">
            <Button
              variant="contained"
              color="success"
              sx={{ "&:hover": { backgroundColor: "#2FB354" }, ml: 2 }}
            >
              Add
            </Button>
          </Link>
        </Box>
      </Box>

      <CustomTable
        columns={columns}
        rows={modifiedRows}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalCount={totalCount}
        handleEdit={handleEdit}
        handleView={handleView}
        handleStatusChange={handleStatusChange}
      />
    </div>
  );
};

export default LeadTemplates;
